export type TakeawayLang = 'it' | 'en' | 'fr' | 'de' | 'es' | 'nl' | 'pt' | 'zh' | 'ja';

export type TakeawaySize = 'small' | 'medium' | 'large';
export type TakeawayLabelKey =
  | 'mixedFry'
  | 'fries'
  | 'croquettes'
  | 'panelle'
  | 'nuggets5'
  | 'nuggets8'
  | 'mozzarelline';

export interface TakeawayCatalogItem {
  name: string;
  priceCents: number;
  familyCents?: number;
  piadinaCents?: number;
  baseName?: string;
  displayName?: string;
  displayNames?: Partial<Record<TakeawayLang, string>>;
  labelKey?: TakeawayLabelKey;
  size?: TakeawaySize;
  syntheticDescription?: keyof typeof TAKEAWAY_SYNTHETIC_DESCRIPTIONS;
}

export interface TakeawayCatalogCategory {
  name: string;
  items: TakeawayCatalogItem[];
}

export const TAKEAWAY_COPY: Record<TakeawayLang, {
  view: string;
  normal: string;
  family: string;
  panino: string;
  piadina: string;
  sauceNote: string;
  sizes: Record<TakeawaySize, string>;
  products: Record<TakeawayLabelKey, string>;
}> = {
  it: {
    view: 'Menù da asporto', normal: 'normale', family: 'famiglia', panino: 'panino', piadina: 'piadina',
    sauceNote: 'Le nostre salse: supplemento da 0,50 € a 1,00 €.',
    sizes: { small: 'piccola', medium: 'media', large: 'grande' },
    products: { mixedFry: 'Fritto misto', fries: 'Patatine', croquettes: 'Crocchette', panelle: 'Panelle', nuggets5: 'Nuggets di pollo ×5 pz.', nuggets8: 'Nuggets di pollo ×8 pz.', mozzarelline: 'Mozzarelline' },
  },
  en: {
    view: 'Takeaway menu', normal: 'regular', family: 'family', panino: 'sandwich', piadina: 'piadina',
    sauceNote: 'Our sauces: supplement from €0.50 to €1.00.',
    sizes: { small: 'small', medium: 'medium', large: 'large' },
    products: { mixedFry: 'Mixed fried platter', fries: 'Fries', croquettes: 'Croquettes', panelle: 'Panelle', nuggets5: 'Chicken nuggets ×5 pcs.', nuggets8: 'Chicken nuggets ×8 pcs.', mozzarelline: 'Mini mozzarella balls' },
  },
  fr: {
    view: 'Menu à emporter', normal: 'normale', family: 'familiale', panino: 'panini', piadina: 'piadina',
    sauceNote: 'Nos sauces : supplément de 0,50 € à 1,00 €.',
    sizes: { small: 'petite', medium: 'moyenne', large: 'grande' },
    products: { mixedFry: 'Friture mixte', fries: 'Frites', croquettes: 'Croquettes', panelle: 'Panelle', nuggets5: 'Nuggets de poulet ×5', nuggets8: 'Nuggets de poulet ×8', mozzarelline: 'Mini-mozzarellas' },
  },
  de: {
    view: 'Speisekarte zum Mitnehmen', normal: 'normal', family: 'Familie', panino: 'Panino', piadina: 'Piadina',
    sauceNote: 'Unsere Saucen: Aufpreis von 0,50 € bis 1,00 €.',
    sizes: { small: 'klein', medium: 'mittel', large: 'groß' },
    products: { mixedFry: 'Gemischte Frittierplatte', fries: 'Pommes frites', croquettes: 'Kroketten', panelle: 'Panelle', nuggets5: 'Chicken Nuggets ×5', nuggets8: 'Chicken Nuggets ×8', mozzarelline: 'Mini-Mozzarella' },
  },
  es: {
    view: 'Carta para llevar', normal: 'normal', family: 'familiar', panino: 'panino', piadina: 'piadina',
    sauceNote: 'Nuestras salsas: suplemento de 0,50 € a 1,00 €.',
    sizes: { small: 'pequeña', medium: 'mediana', large: 'grande' },
    products: { mixedFry: 'Frito mixto', fries: 'Patatas fritas', croquettes: 'Croquetas', panelle: 'Panelle', nuggets5: 'Nuggets de pollo ×5', nuggets8: 'Nuggets de pollo ×8', mozzarelline: 'Mini mozzarellas' },
  },
  nl: {
    view: 'Afhaalmenu', normal: 'normaal', family: 'familie', panino: 'panino', piadina: 'piadina',
    sauceNote: 'Onze sauzen: toeslag van €0,50 tot €1,00.',
    sizes: { small: 'klein', medium: 'middel', large: 'groot' },
    products: { mixedFry: 'Gemengde frituur', fries: 'Friet', croquettes: 'Kroketten', panelle: 'Panelle', nuggets5: 'Kipnuggets ×5', nuggets8: 'Kipnuggets ×8', mozzarelline: 'Mini-mozzarella' },
  },
  pt: {
    view: 'Menu para levar', normal: 'normal', family: 'família', panino: 'panino', piadina: 'piadina',
    sauceNote: 'Os nossos molhos: suplemento de 0,50 € a 1,00 €.',
    sizes: { small: 'pequena', medium: 'média', large: 'grande' },
    products: { mixedFry: 'Frito misto', fries: 'Batatas fritas', croquettes: 'Croquetes', panelle: 'Panelle', nuggets5: 'Nuggets de frango ×5', nuggets8: 'Nuggets de frango ×8', mozzarelline: 'Mini mozzarellas' },
  },
  zh: {
    view: '外带菜单', normal: '普通', family: '家庭装', panino: '帕尼尼', piadina: '皮亚迪纳',
    sauceNote: '本店酱料：加价 0.50 € 至 1.00 €。',
    sizes: { small: '小份', medium: '中份', large: '大份' },
    products: { mixedFry: '什锦炸物', fries: '薯条', croquettes: '炸丸子', panelle: '西西里鹰嘴豆饼', nuggets5: '鸡块 ×5', nuggets8: '鸡块 ×8', mozzarelline: '迷你马苏里拉球' },
  },
  ja: {
    view: 'テイクアウトメニュー', normal: '通常', family: 'ファミリー', panino: 'パニーノ', piadina: 'ピアディーナ',
    sauceNote: '当店のソース：追加料金 0.50 €〜1.00 €。',
    sizes: { small: '小', medium: '中', large: '大' },
    products: { mixedFry: 'フリットミスト', fries: 'フライドポテト', croquettes: 'コロッケ', panelle: 'パネッレ', nuggets5: 'チキンナゲット ×5', nuggets8: 'チキンナゲット ×8', mozzarelline: 'ミニモッツァレラ' },
  },
};

export const TAKEAWAY_SYNTHETIC_DESCRIPTIONS = {
  braccio: {
    it: 'Pomodoro, fiordilatte, spinaci, scaglie di grana.',
    en: 'Tomato, mozzarella, spinach, Parmesan shavings.',
    fr: 'Tomate, mozzarella, épinards, copeaux de parmesan.',
    de: 'Tomaten, Mozzarella, Spinat, Parmesanspäne.',
    es: 'Tomate, mozzarella, espinacas, lascas de parmesano.',
    nl: 'Tomaat, mozzarella, spinazie, Parmezaanse schilfers.',
    pt: 'Tomate, mozzarella, espinafre, lascas de parmesão.',
    zh: '番茄、马苏里拉奶酪、菠菜、帕尔马干酪片。',
    ja: 'トマト、モッツァレラ、ほうれん草、パルメザンチーズスライス。',
  },
  delizia: {
    it: 'Pomodoro, fiordilatte, crema di carciofi, speck, svizzero.',
    en: 'Tomato, mozzarella, artichoke cream, speck, Swiss cheese.',
    fr: 'Tomate, mozzarella, crème d’artichauts, speck, fromage suisse.',
    de: 'Tomaten, Mozzarella, Artischockencreme, Speck, Schweizer Käse.',
    es: 'Tomate, mozzarella, crema de alcachofas, speck, queso suizo.',
    nl: 'Tomaat, mozzarella, artisjokkencrème, speck, Zwitserse kaas.',
    pt: 'Tomate, mozzarella, creme de alcachofra, speck, queijo suíço.',
    zh: '番茄、马苏里拉奶酪、洋蓟酱、烟熏火腿、瑞士奶酪。',
    ja: 'トマト、モッツァレラ、アーティチョーククリーム、スペック、スイスチーズ。',
  },
  funghi: {
    it: 'Pomodoro, fiordilatte, funghi freschi.',
    en: 'Tomato, mozzarella, fresh mushrooms.',
    fr: 'Tomate, mozzarella, champignons frais.',
    de: 'Tomaten, Mozzarella, frische Pilze.',
    es: 'Tomate, mozzarella, champiñones frescos.',
    nl: 'Tomaat, mozzarella, verse champignons.',
    pt: 'Tomate, mozzarella, cogumelos frescos.',
    zh: '番茄、马苏里拉奶酪、新鲜蘑菇。',
    ja: 'トマト、モッツァレラ、フレッシュマッシュルーム。',
  },
  mediterranea: {
    it: 'Pomodoro, bufala, zucchine, gamberetti, ciliegino.',
    en: 'Tomato, buffalo mozzarella, zucchini, shrimp, cherry tomatoes.',
    fr: 'Tomate, mozzarella de bufflonne, courgettes, crevettes, tomates cerises.',
    de: 'Tomaten, Büffelmozzarella, Zucchini, Garnelen, Kirschtomaten.',
    es: 'Tomate, mozzarella de búfala, calabacín, gambas, tomates cherry.',
    nl: 'Tomaat, buffelmozzarella, courgette, garnalen, cherrytomaten.',
    pt: 'Tomate, mozzarella de búfala, curgete, camarões, tomate-cereja.',
    zh: '番茄、水牛马苏里拉奶酪、西葫芦、虾仁、樱桃番茄。',
    ja: 'トマト、水牛モッツァレラ、ズッキーニ、エビ、チェリートマト。',
  },
  veneta: {
    it: 'Pomodoro, fiordilatte, radicchio rosso, salsiccia, provola affumicata.',
    en: 'Tomato, mozzarella, red radicchio, sausage, smoked provola.',
    fr: 'Tomate, mozzarella, radicchio rouge, saucisse, provola fumée.',
    de: 'Tomaten, Mozzarella, roter Radicchio, Wurst, geräucherte Provola.',
    es: 'Tomate, mozzarella, radicchio rojo, salchicha, provola ahumada.',
    nl: 'Tomaat, mozzarella, rode radicchio, worst, gerookte provola.',
    pt: 'Tomate, mozzarella, radicchio vermelho, linguiça, provola fumada.',
    zh: '番茄、马苏里拉奶酪、红菊苣、香肠、烟熏普罗沃拉奶酪。',
    ja: 'トマト、モッツァレラ、赤ラディッキオ、ソーセージ、燻製プロヴォーラ。',
  },
  wurstel: {
    it: 'Pomodoro, fiordilatte, wurstel.',
    en: 'Tomato, mozzarella, wurstel.',
    fr: 'Tomate, mozzarella, wurstel.',
    de: 'Tomaten, Mozzarella, Würstchen.',
    es: 'Tomate, mozzarella, salchicha.',
    nl: 'Tomaat, mozzarella, knakworst.',
    pt: 'Tomate, mozzarella, salsicha.',
    zh: '番茄、马苏里拉奶酪、香肠。',
    ja: 'トマト、モッツァレラ、ソーセージ。',
  },
} as const;

const item = (
  name: string,
  priceCents: number,
  familyCents?: number,
  extra: Partial<TakeawayCatalogItem> = {},
): TakeawayCatalogItem => ({ name, priceCents, familyCents, ...extra });

export const LO_SPERDICCHIO_TAKEAWAY: TakeawayCatalogCategory[] = [
  {
    name: 'Pizze Speciali',
    items: [
      item('Aragonese', 1000), item('Boscaiola', 1100), item('Botte', 1200), item('Bronte', 1200),
      item('Burrata', 1200), item('Ciccio', 1100), item('Concy', 1100), item('Mortazza', 1000),
      item('Regina', 900), item('Salsiccia e friarielli', 1000), item('Sperdicchio', 900), item('Tronchetto', 1200),
    ],
  },
  {
    name: 'Calzoni',
    items: [item('Liscio', 800), item('Farcito', 850), item('Olivia', 850), item('Sfizioso', 850)],
  },
  {
    name: 'Pizze Classiche',
    items: [
      item('4 formaggi', 700, 1600), item('4 stagioni', 750, 1700), item('Bacon', 850, 1900),
      item('Braccio di ferro', 700, 1600, { syntheticDescription: 'braccio' }), item('Bufala', 600, 1400),
      item('Calò', 800, 1800), item('Capricciosa', 800, 1800), item('Carbonara', 800, 1800),
      item('Classica', 700, 1600), item('Covaccio', 800, 1800),
      item('Crudaiola', 900, 2000, { baseName: 'Cruda', displayName: 'Crudaiola' }),
      item('Cudduruni', 600, 1400), item('Delizia', 800, 1800, { syntheticDescription: 'delizia' }),
      item('Diavola', 700, 1600), item('Francescana', 850, 1900), item('Frutti di mare', 1200),
      item('Funghi', 600, 1400, { syntheticDescription: 'funghi' }), item('Golosa', 850, 1900),
      item('Margherita', 500, 1300), item('Marianna', 1000, 2200),
      item('Mediterranea', 1000, 2200, { syntheticDescription: 'mediterranea' }),
      item('Messicana', 850, 1900), item('Mollica', 850, 1800),
      item('Ortolana', 850, 1800, { baseName: 'Ottiolana', displayName: 'Ortolana' }),
      item('Pachino', 850, 1900), item('Parmigiana', 700, 1600), item('Porcini', 900, 2000),
      item('Romana', 650, 1500), item('Rucola', 900, 2000), item('Salmone', 1000),
      item('Saporita', 850, 1900), item('Tartufata', 1000, 2200), item('Tedesca', 600, 1400),
      item('Texana', 850, 1900), item('Tiffani', 900, 2200), item('Tonno e cipolla', 750, 1800),
      item('Veneta', 900, 2000, { syntheticDescription: 'veneta' }), item('Vulcano', 850, 1900),
      item('Wurstel', 600, 1400, { syntheticDescription: 'wurstel' }),
    ],
  },
  {
    name: 'Focacce',
    items: [item('F1', 850), item('F2', 900), item('F3', 900), item('F4', 900), item('F5', 900)],
  },
  {
    name: 'Pizze dolci',
    items: [item('Nutellosa', 700), item('Pistacchiosa', 700)],
  },
  {
    name: 'Antipasti',
    items: [
      item('Patatine piccola', 250, undefined, { labelKey: 'fries', size: 'small' }),
      item('Patatine media', 500, undefined, { labelKey: 'fries', size: 'medium' }),
      item('Patatine grande', 800, undefined, { labelKey: 'fries', size: 'large' }),
      item('Crocchette piccola', 300, undefined, { labelKey: 'croquettes', size: 'small' }),
      item('Crocchette media', 600, undefined, { labelKey: 'croquettes', size: 'medium' }),
      item('Panelle media', 400, undefined, { baseName: 'Panelle siciliane', labelKey: 'panelle', size: 'medium' }),
      item('Panelle grande', 800, undefined, { baseName: 'Panelle siciliane', labelKey: 'panelle', size: 'large' }),
      item('Nuggets di pollo ×5 pz.', 350, undefined, { labelKey: 'nuggets5' }),
      item('Nuggets di pollo ×8 pz.', 650, undefined, { labelKey: 'nuggets8' }),
      item('Mozzarelline piccola', 400, undefined, { labelKey: 'mozzarelline', size: 'small' }),
      item('Fritto misto medio', 700, undefined, { baseName: 'Fritto misto ×2', labelKey: 'mixedFry', size: 'medium' }),
      item('Fritto misto grande', 900, undefined, { baseName: 'Fritto misto ×4', labelKey: 'mixedFry', size: 'large' }),
    ],
  },
  {
    name: 'Burger menù',
    items: [
      item('Big Burger', 1100), item('Etna', 1100), item('Bomba', 1100), item('Brontolo', 1100),
      item('Dotto', 1100), item('Sperdi BBQ', 1100), item('Sperdichicken', 1100), item('Eolo', 1100),
      item('Happy sorpresa', 850),
    ],
  },
  {
    name: 'Panini e piadine',
    items: [
      item('Al tonno', 600, undefined, { baseName: 'Al tonno — panino', displayName: 'Al tonno', piadinaCents: 700, displayNames: { en: 'Tuna', fr: 'Au thon', de: 'Mit Thunfisch', es: 'De atún', nl: 'Tonijn', pt: 'Al tonno', zh: '金枪鱼', ja: 'ツナ' } }),
      item('Tieni forte', 650, undefined, { baseName: 'Tieni forte — panino', displayName: 'Tieni forte', piadinaCents: 750 }),
      item('Classico', 650, undefined, { baseName: 'Classico — panino', displayName: 'Classico', piadinaCents: 750 }),
      item('Contadino', 650, undefined, { baseName: 'Contadino — panino', displayName: 'Contadino', piadinaCents: 750 }),
      item('Sperdicchio', 600, undefined, { baseName: 'Sperdicchio — panino', displayName: 'Sperdicchio', piadinaCents: 700 }),
      item('Agrodolce', 600, undefined, { baseName: 'Agrodolce — panino', displayName: 'Agrodolce', piadinaCents: 700 }),
      item('Giagià', 600, undefined, { baseName: 'Giagià — panino', displayName: 'Giagià', piadinaCents: 700 }),
      item('Bacon', 600, undefined, { baseName: 'Bacon — panino', displayName: 'Bacon', piadinaCents: 700 }),
      item('Nice', 600, undefined, { baseName: 'Nice — panino', displayName: 'Nice', piadinaCents: 700 }),
      item('Kebab', 700, undefined, { baseName: 'Kebab — panino', displayName: 'Kebab', piadinaCents: 800 }),
      item('Cartoccio', 600, undefined, { baseName: 'Cartoccio — panino', displayName: 'Cartoccio', piadinaCents: 700 }),
      item('Crispy', 600, undefined, { baseName: 'Crispy — panino', displayName: 'Crispy', piadinaCents: 700 }),
      item('Con patatine', 300, undefined, { baseName: 'Con patatine — panino', displayName: 'Con patatine', piadinaCents: 400 }),
      item('Con patatine e wurstel', 400, undefined, { displayName: 'Con patatine e wurstel', piadinaCents: 450, displayNames: { en: 'With fries and wurstel', fr: 'Avec frites et wurstel', de: 'Mit Pommes und Würstchen', es: 'Con patatas y salchicha', nl: 'Met friet en worst', pt: 'Com batatas e salsicha', zh: '配薯条和香肠', ja: 'フライドポテトとソーセージ入り' } }),
      item('Con panelle Sale e Pepe', 300, undefined, { baseName: 'Con panelle Sale e Pepe', displayName: 'Con panelle Sale e Pepe' }),
    ],
  },
];

// Keys use the original catalogue names, never translations or array positions.
export function takeawayPriceKey(category: string, itemName: string): string {
  return `${category}/${itemName}`;
}

export interface TakeawayPrice {
  priceCents: number;
  variantCents: number | null;
}
export type TakeawayPrices = Record<string, TakeawayPrice>;

export function parseMenuPrice(value: string): number {
  const normalized = value.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) throw new Error('Inserisci un prezzo valido, con al massimo due decimali.');
  const cents = Math.round(Number(normalized) * 100);
  if (!Number.isSafeInteger(cents) || cents > 1000000) throw new Error('Il prezzo massimo è 10.000 €.');
  return cents;
}

export function resolveTakeawayPrice(item: TakeawayCatalogItem, override?: TakeawayPrice): TakeawayCatalogItem {
  const valid = (n: unknown): n is number => typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 && n <= 1000000;
  return {
    ...item,
    priceCents: valid(override?.priceCents) ? override.priceCents : item.priceCents,
    ...(item.familyCents != null && valid(override?.variantCents) ? { familyCents: override.variantCents } : {}),
    ...(item.piadinaCents != null && valid(override?.variantCents) ? { piadinaCents: override.variantCents } : {}),
  };
}

export function parseTakeawayVariant(priceUnit: string | null | undefined): { kind: 'family' | 'piadina'; cents: number } | null {
  const match = /^takeaway:(family|piadina):(\d+)$/.exec(priceUnit ?? '');
  return match ? { kind: match[1] as 'family' | 'piadina', cents: Number(match[2]) } : null;
}
